// Full-palette swatch grid: every color family x every Tailwind stop

import React from 'react';
import {
  COLOR_SCALES,
  COLOR_STOPS,
  getColorStop,
  type ColorFamily,
  type ColorStop,
} from '@/styles/colorStops';
import { cn } from '@/utilities/cn';

const COLOR_FAMILIES = Object.keys(COLOR_SCALES) as ColorFamily[];

interface DrawingColorPickerProps {
  family: ColorFamily;
  stop: ColorStop;
  onChange: (family: ColorFamily, stop: ColorStop) => void;
}

export const DrawingColorPicker: React.FC<DrawingColorPickerProps> = ({
  family,
  stop,
  onChange,
}) => {
  const selectedRef = React.useRef<HTMLButtonElement>(null);

  // Bring the current selection into view when the picker opens
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  return (
    <div className="max-h-72 overflow-y-auto">
      <div className="flex flex-col gap-1 p-1">
        {COLOR_FAMILIES.map((rowFamily) => (
          <div key={rowFamily} className="flex items-center gap-1">
            {COLOR_STOPS.map((rowStop) => {
              const selected = rowFamily === family && rowStop === stop;
              return (
                <button
                  key={rowStop}
                  ref={selected ? selectedRef : undefined}
                  type="button"
                  aria-label={`${rowFamily} ${rowStop}`}
                  aria-pressed={selected}
                  className={cn(
                    'size-6 shrink-0 cursor-pointer rounded-sm ring-1 ring-foreground/10 ring-inset transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    selected && 'ring-2 ring-ring scale-110'
                  )}
                  style={{ backgroundColor: getColorStop(rowFamily, rowStop) }}
                  onClick={() => onChange(rowFamily, rowStop)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
